
  if (!customElements.get('wtm-birthstone-native')) {
    customElements.define('wtm-birthstone-native', class extends HTMLElement {
      connectedCallback() {
        queueMicrotask(() => { if (this.isConnected) this.initialize(); });
      }

      disconnectedCallback() {
        this.controller?.abort();
        this.galleryPreview?.remove();
        this.galleryToggle?.remove();
        this.mobileRating?.classList.remove('bsn-rating-row');
        if (this.galleryFrame?.isConnected) this.galleryFrame.before(this.gallery);
        this.galleryFrame?.remove();
        if (this.gallery) this.gallery.hidden = false;
        this.initialized = false;
      }

      find(selector) {
        return this.querySelector(selector) || this.section?.querySelector(selector) || null;
      }

      findAll(selector) {
        return Array.from((this.section || this).querySelectorAll(selector));
      }

      initializeGallery() {
        this.gallery = this.section?.querySelector('.wtm-product-gallery');
        if (!this.gallery) return;
        this.previewActivated = false;
        this.galleryPreview = document.createElement('div');
        this.galleryPreview.id = this.gallery.id + '-ring-preview';
        this.galleryPreview.className = 'bsn-native-preview bsn-gallery-preview';
        this.galleryPreview.dataset.bspNativePreview = '';
        this.galleryPreview.setAttribute('role', 'group');
        this.galleryPreview.setAttribute('aria-label', this.strings.ringPreview);
        this.galleryPreview.hidden = true;
        this.galleryToggle = document.createElement('button');
        this.galleryToggle.type = 'button';
        this.galleryToggle.className = 'bsn-gallery-toggle';
        this.galleryToggle.dataset.bspGalleryToggle = '';
        this.galleryToggle.setAttribute('aria-controls', this.gallery.id + ' ' + this.galleryPreview.id);
        const icons = {
          gallery: '<rect x="3" y="3" width="18" height="18" rx="3"/><circle cx="8.5" cy="8.5" r="1.5"/><path d="m21 15-5-5L5 21"/>',
          preview: '<path d="M3 8 7 3h10l4 5-9 13L3 8Zm0 0h18M7 3l5 18L17 3"/>'
        };
        for (const [mode, text, shortText] of [['gallery', this.strings.showGallery, this.strings.galleryLabel], ['preview', this.strings.showPreview, this.strings.previewLabel]]) {
          const state = document.createElement('span');
          state.className = 'bsn-gallery-toggle-state';
          state.dataset.bspToggleState = mode;
          state.innerHTML = '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true" focusable="false">' + icons[mode] + '</svg>';
          const label = document.createElement('span');
          label.className = 'bsn-gallery-label';
          label.textContent = text;
          const mobileLabel = document.createElement('span');
          mobileLabel.className = 'bsn-gallery-label-mobile';
          mobileLabel.textContent = shortText;
          state.append(label, mobileLabel);
          this.galleryToggle.appendChild(state);
        }
        this.galleryFrame = document.createElement('div');
        this.galleryFrame.className = 'bsn-gallery-frame';
        this.gallery.before(this.galleryFrame);
        this.galleryFrame.append(this.galleryPreview, this.gallery);
        this.mobileRating = this.section.querySelector('.featured-product-mobile-review');
        this.mobileRating?.classList.add('bsn-rating-row');
        const mobile = window.matchMedia('(max-width: 767px)');
        const placeToggle = () => {
          if (mobile.matches && this.mobileRating) this.mobileRating.appendChild(this.galleryToggle);
          else this.galleryFrame.after(this.galleryToggle);
        };
        placeToggle();
        mobile.addEventListener('change', placeToggle, { signal: this.controller.signal });
        this.galleryToggle.addEventListener('click', () => this.setGalleryView(!this.previewVisible), { signal: this.controller.signal });
        this.setGalleryView(false);
      }

      setGalleryView(showPreview) {
        if (!this.gallery || !this.galleryPreview || !this.galleryToggle) return;
        this.previewVisible = showPreview;
        if (showPreview) {
          this.previewActivated = true;
          this.renderGallery();
        }
        this.gallery.hidden = showPreview;
        this.galleryPreview.hidden = !showPreview;
        this.galleryToggle.setAttribute('aria-label', showPreview ? this.strings.showGallery : this.strings.showPreview);
        this.galleryToggle.querySelectorAll('[data-bsp-toggle-state]').forEach(state => {
          state.setAttribute('aria-hidden', String(state.dataset.bspToggleState !== (showPreview ? 'gallery' : 'preview')));
        });
        if (!showPreview) {
          const slider = this.gallery.querySelector('.main-product-image-slider')?.slick;
          if (slider && !slider.unslicked) slider.setPosition();
        }
      }

      mountPreview() {
        if (this.previewActivated) this.renderGallery();
      }

      publishState() {
        if (!this.offer) return;
        const quote = this.lastQuote;
        const apiReady = typeof window.WTMCartDrawer?.addVariant === 'function';
        const disabled = !this.validSelection || Boolean(this.busy) || !apiReady;
        const needsSelection = this.validCatalog && Boolean(quote) &&
          !this.hasAttribute('data-bsp-pricing-pending') &&
          this.currentVariant?.available === true && !this.currentVariant.requires_selling_plan &&
          this.selections.slice(0, this.selectedTier).some(pair => pair.some(value => !value));
        this.submitButton.disabled = disabled;
        this.submitButton.setAttribute('aria-busy', String(Boolean(this.busy)));
        if (this.busy) this.submitText.textContent = this.strings.updating || 'UPDATING CART…';
        else if (this.validSelection && quote) {
          let price = this.submitText.querySelector('[data-atc-price]');
          if (!price) {
            price = document.createElement('span'); price.dataset.atcPrice = '';
            this.submitText.replaceChildren(document.createTextNode(this.strings.addToCart + ' — '),price);
          }
          price.textContent = this.plainMoney(quote.price);
        }
        const state = {
          productId: Number(this.config.productId), offerId: this.offer.id, offerNumber: this.selectedTier,
          variantId: this.validSelection ? Number(this.variantInput.value) : 0,
          price: quote?.priceAmount ?? null, compareAtPrice: quote?.compareAmount ?? null,
          saving: quote?.savingAmount > 0 ? this.strings.savingTemplate.replace('[amount]', quote.saving) : '',
          currency: this.config.presentmentCurrency, estimated: false,
          busy: Boolean(this.busy), disabled,
          action: this.busy ? 'updating' : needsSelection ? 'select' : disabled ? 'unavailable' : 'add'
        };
        const key = JSON.stringify(state);
        if (key === this.stateKey) return;
        this.stateKey = key;
        this.offerState = Object.freeze(state);
        this.offer.dispatchEvent(new CustomEvent('wtm:offer-state', { detail: this.offerState, bubbles: true }));
      }

      initialize() {
        if (this.initialized) return;
        this.initialized = true;
        this.controller = new AbortController();
        this.section = this.closest('.shopify-section');
        this.offer = this.closest('wtm-offer-box');
        this.form = this.find('form');
        if (!this.form) return;
        this.variantInput = this.find('[data-bsp-variant]');
        this.submitButton = this.find('[data-bsp-submit]');
        this.submitText = this.find('[data-bsp-submit-text]');
        this.submitPrice = this.find('[data-bsp-submit-price]');
        this.savingBadge = this.find('[data-bsp-saving]');
        this.propertyInputs = { first: this.find('[data-bsp-property="first"]'), second: this.find('[data-bsp-property="second"]') };
        this.status = this.find('[data-bsp-status]');
        this.choices = Array.from(this.querySelectorAll('[data-bsp-option]'));
        // Read the shared color choices from the first offer's selectors.
        this.radios = this.choices.filter(choice => choice.closest('[data-bsp-panel]').dataset.bspPanel === '1');
        this.selectedTier = 1;
        this.offerOpened = true;
        this.selections = Array.from({ length: 3 }, () => ['', '']);
        this.strings = {};
        this.validCatalog = false;
        try {
          this.config = JSON.parse(this.find('[data-bsp-config]').textContent);
          this.strings = this.config.strings;
          this.bundleTotals = JSON.parse(this.config.bundleTotalsJSON);
          this.bundleDiscounts = JSON.parse(this.config.bundleDiscountsJSON);
          if (!Array.isArray(this.bundleDiscounts) || this.bundleDiscounts.length !== 3 || this.bundleDiscounts.some(value => !Number.isSafeInteger(value) || value < 0)) throw new Error('Invalid bundle discounts');
          if (!Array.isArray(this.bundleTotals) || this.bundleTotals.length !== 3 || this.bundleTotals.some(value => !Number.isSafeInteger(value) || value <= 0)) throw new Error('Invalid offer totals');
          this.palette = this.makePalette(this.config.paletteJson);
          this.overlayUrls = this.readOverlayManifest(this.config.overlayManifestJson);
          this.catalog = this.buildCatalog(this.config);
          this.validCatalog = true;
        } catch (_) {
          this.failClosed(this.strings.configurationError || this.status.textContent);
          return;
        }
        this.offer.config = { productId: this.config.productId, isProductPage: true };
        this.offer.getOfferState = () => this.offerState || null;
        this.initializeGallery();
        const signal = this.controller.signal;
        const syncSaleTitle = () => {
          const title = this.find('[data-bsp-sale-title]');
          if (title) title.textContent = document.querySelector('wtm-sale-announcement .wtm-sale-bar__heading')?.textContent.trim() || '';
        };
        syncSaleTitle();
        document.addEventListener('shopify:section:load',syncSaleTitle,{ signal });
        this.form.addEventListener('click', event => {
          const tier = event.target.closest('[data-bsp-tier]');
          const opener = event.target.closest('[data-bsp-open]');
          const choice = event.target.closest('[data-bsp-option]');
          if (tier) {
            this.closePickers();
            this.selectedTier = Number(tier.dataset.bspTier);
            this.offerOpened = true;
            this.setGalleryView(true);
            this.update();
          } else if (opener) {
            const open = opener.getAttribute('aria-expanded') !== 'true';
            this.closePickers();
            if (open) {
              document.getElementById(opener.getAttribute('aria-controls')).hidden = false;
              opener.setAttribute('aria-expanded', 'true');
            }
          } else if (choice) {
            this.selections[Number(choice.dataset.ring) - 1][choice.dataset.bspOption === 'first' ? 0 : 1] = choice.value;
            this.closePickers(true);
            this.setGalleryView(true);
            this.update();
          }
        }, { signal });
        this.form.addEventListener('keydown', event => {
          if (event.key === 'Escape') { this.closePickers(true); return; }
          const picker = event.target.closest('.concept-picker');
          if (!picker || !['ArrowDown', 'ArrowUp', 'ArrowLeft', 'ArrowRight', 'Home', 'End'].includes(event.key)) return;
          event.preventDefault();
          const opener = picker.querySelector('[data-bsp-open]');
          const menu = document.getElementById(opener.getAttribute('aria-controls'));
          this.closePickers(); menu.hidden = false; opener.setAttribute('aria-expanded', 'true');
          const options = Array.from(menu.querySelectorAll('[data-bsp-option]'));
          let index = options.indexOf(document.activeElement);
          index = event.key === 'Home' ? 0 : event.key === 'End' ? options.length - 1 : (index + (['ArrowUp', 'ArrowLeft'].includes(event.key) ? -1 : 1) + options.length) % options.length;
          options[index].focus();
        }, { signal });
        document.addEventListener('click', event => {
          if (event.defaultPrevented && event.target.closest?.('[data-wtm-sticky-submit]')?.closest('wtm-sticky-atc')?.offer === this.offer) return;
          if (!event.target.closest?.('.concept-picker')) this.closePickers();
        }, { signal });
        this.form.addEventListener('focusout', () => queueMicrotask(() => {
          if (!this.querySelector('.concept-picker:focus-within')) this.closePickers();
        }), { signal });
        this.form.addEventListener('submit', async event => {
          event.preventDefault();
          if (this.busy) return;
          this.update();
          if (!this.validSelection) return;
          this.busy = true; this.publishState();
          try {
            const properties = this.selectionProperties();
            if (this.bundleMode) properties._wtm_birthstone_set = crypto.randomUUID();
            const result = await window.WTMCartDrawer.addVariant({
              id: Number(this.variantInput.value), quantity: 1, properties
            }, this.submitButton);
            if (!result?.added) throw new Error(this.strings.configurationError);
          } catch (error) {
            this.status.textContent = error?.message || this.strings.configurationError;
            this.setAttribute('data-bsp-error', '');
          } finally { this.busy = false; this.publishState(); }
        }, { signal });
        document.addEventListener('click', event => {
          const trigger = event.target.closest?.('[data-wtm-sticky-submit]');
          if (!trigger || trigger.closest('wtm-sticky-atc')?.offer !== this.offer) return;
          event.preventDefault();
          if (this.busy) return;
          this.update();
          if (this.offerState?.action === 'select') {
            const ring = this.selections.slice(0, this.selectedTier).findIndex(pair => pair.some(value => !value));
            const side = this.selections[ring][0] ? 'second' : 'first';
            this.offerOpened = true;
            this.closePickers();
            this.syncPickers();
            const opener = this.querySelector(`[data-bsp-panel="${this.selectedTier}"] [data-bsp-open][data-ring="${ring + 1}"][data-side="${side}"]`);
            if (opener) {
              opener.focus({ preventScroll: true });
              opener.scrollIntoView({ block: 'center', behavior: window.matchMedia('(prefers-reduced-motion: reduce)').matches ? 'instant' : 'smooth' });
              opener.click();
            }
          } else if (this.validSelection && !this.submitButton.disabled) {
            this.form.requestSubmit(this.submitButton);
          }
        }, { capture: true, signal });
        window.addEventListener('popstate', () => { this.restoreVariant(new URL(location.href).searchParams.get('variant')); this.update(); }, { signal });
        window.addEventListener('pageshow', () => this.update(), { signal });
        this.choices.forEach(choice => choice.querySelector('.concept-gem').style.setProperty('--stone', this.colorFor(choice.value)));
        this.restoreVariant(new URL(location.href).searchParams.get('variant') || this.config.initialVariantId);
        this.update();
      }

      makePalette(raw) {
        const palette = {
          january: '#8d1736', february: '#8240af', march: '#48c2e8', april: '#eef4fa',
          may: '#168b49', june: '#bd8ee0', july: '#cc2545', august: '#9dcb3b',
          september: '#1d3dba', october: '#e5a1c4', november: '#e4b935', december: '#636bd7'
        };
        if (raw?.trim()) {
          const overrides = JSON.parse(raw);
          Object.entries(overrides).forEach(([month, hex]) => {
            if (!(month.toLowerCase() in palette) || !/^#[0-9a-f]{6}$/i.test(hex)) throw new Error('Invalid preview palette');
            palette[month.toLowerCase()] = hex;
          });
        }
        return palette;
      }



      selectionProperties() {
        if (!this.bundleMode) return { 'First Birthstone': this.selections[0][0], 'Second Birthstone': this.selections[0][1] };
        return Object.fromEntries(this.selections.slice(0, this.selectedTier).flatMap((pair, index) =>
          pair.map((value, side) => [`Ring ${index + 1} - ${side === 0 ? 'First' : 'Second'} Birthstone`, value])));
      }

      syncProperties(active = false) {
        const holder = this.find('[data-bsp-properties]');
        holder.replaceChildren();
        if (!active) return;
        Object.entries(this.selectionProperties()).forEach(([key, value]) => {
          const input = document.createElement('input');
          input.type = 'hidden'; input.name = `properties[${key}]`; input.value = value;
          holder.appendChild(input);
        });
      }

      colorFor(value) {
        const month = this.monthFor(value);
        return this.palette[month] || null;
      }

      monthFor(value) {
        return String(value).trim().toLowerCase().match(/^[a-z]+/)?.[0] || '';
      }

      readOverlayManifest(raw) {
        const result = { first: {}, second: {} };
        if (!raw?.trim()) return result;
        try {
          const manifest = JSON.parse(raw);
          ['first', 'second'].forEach(side => {
            Object.entries(manifest[side] || {}).forEach(([month, value]) => {
              const url = new URL(value, window.location.origin);
              if (month.toLowerCase() in this.palette && url.protocol === 'https:') result[side][month.toLowerCase()] = url.href;
            });
          });
        } catch (_) {}
        return result;
      }

      buildCatalog(config) {
        this.bundleMode = config.variants?.length === 3 && config.optionNames?.length === 1;
        if (this.bundleMode) {
          const map = new Map();
          config.variants.forEach(variant => {
            const number = Number(variant.title?.match(/^Buy ([1-3])$/)?.[1]);
            if (!number || map.has(number) || !variant.id || !Number.isSafeInteger(variant.price)) throw new Error('Invalid bundle catalog');
            map.set(number, variant);
          });
          if (map.size !== 3 || this.radios.length !== 24) throw new Error('Incomplete bundle catalog');
          return map;
        }
        if (!Array.isArray(config.optionNames) || config.optionNames.length !== 2 || !Array.isArray(config.variants)) throw new Error('Invalid options');
        this.firstIndex = config.optionNames.indexOf(config.firstOptionName);
        this.secondIndex = config.optionNames.indexOf(config.secondOptionName);
        if (this.firstIndex < 0 || this.secondIndex < 0 || this.firstIndex === this.secondIndex) throw new Error('Missing birthstone options');
        const first = this.radios.filter(radio => radio.dataset.bspOption === 'first').map(radio => radio.value);
        const second = this.radios.filter(radio => radio.dataset.bspOption === 'second').map(radio => radio.value);
        if (first.length !== 12 || second.length !== 12 || new Set(first).size !== 12 || new Set(second).size !== 12) throw new Error('Incomplete birthstone options');
        if (config.variantCount !== 144 || config.variants.length !== 144) throw new Error('Incomplete variant catalog');
        const map = new Map();
        const ids = new Set();
        config.variants.forEach(variant => {
          if (!variant.id || !Array.isArray(variant.options) || variant.options.length !== 2) throw new Error('Invalid variant');
          const a = variant.options[this.firstIndex];
          const b = variant.options[this.secondIndex];
          const key = JSON.stringify([a, b]);
          if (!first.includes(a) || !second.includes(b) || map.has(key) || ids.has(String(variant.id)) || !config.prices[String(variant.id)]?.price) throw new Error('Ambiguous variant');
          map.set(key, variant);
          ids.add(String(variant.id));
        });
        first.forEach(a => second.forEach(b => { if (!map.has(JSON.stringify([a, b]))) throw new Error('Missing combination'); }));
        return map;
      }

      restoreVariant(id) {
        if (!id) return;
        const variant = this.config.variants.find(item => String(item.id) === String(id));
        if (this.bundleMode) { if (variant) this.selectedTier = Number(variant.title.match(/^Buy ([1-3])$/)?.[1] || 1); return; }
        if (variant) this.selections[0] = [variant.options[this.firstIndex], variant.options[this.secondIndex]];
      }

      failClosed(message, buttonText = this.strings.unavailableButton || message) {
        this.validSelection = false;
        this.variantInput.value = '';
        this.variantInput.disabled = true;
        this.syncProperties();
        this.submitButton.disabled = true;
        this.status.textContent = message;
        this.submitText.textContent = buttonText;
        this.setAttribute('data-bsp-error', '');
        this.publishState();
      }

      update() {
        if (!this.validCatalog) return;
        this.syncPickers();
        this.mountPreview();
        const pairs = this.selections.slice(0, this.selectedTier);
        this.selectedVariants = this.bundleMode ? [this.catalog.get(this.selectedTier)] : pairs.map(pair => pair.every(Boolean) ? this.catalog.get(JSON.stringify(pair)) : null);
        this.renderPrice(this.selectedVariants[0] || this.config.variants[0]);
        const pending = this.selectedTier > 1 && this.config.bundleDiscountsReady !== true;
        this.toggleAttribute('data-bsp-pricing-pending', pending);
        if (pending) { this.failClosed(this.strings.pricingPending); this.submitText.textContent = this.strings.unavailableButton; return; }
        if (!pairs.every(pair => pair.every(Boolean))) {
          this.failClosed(this.strings.chooseAll, this.strings.chooseBoth); this.removeAttribute('data-bsp-error'); return;
        }
        if (!this.lastQuote || this.selectedVariants.some(variant => !variant || variant.available !== true || variant.requires_selling_plan === true)) {
          this.failClosed(this.strings.unavailable); return;
        }
        this.validSelection = true;
        this.variantInput.value = String(this.selectedVariants[0].id);
        this.variantInput.disabled = false;
        this.syncProperties(true);
        this.form.elements.quantity.value = '1';
        this.status.textContent = this.strings.ready;
        this.removeAttribute('data-bsp-error');
        this.publishState();
      }

      convertUSD(amount) {
        const currency = this.config.presentmentCurrency;
        if (this.config.shopCurrency !== 'USD') return null;
        if (currency === 'USD') return amount;
        const fx = window.Shopify?.currency;
        const rate = Number(fx?.rate);
        if (fx?.active !== currency || !Number.isFinite(rate) || rate <= 0 || !Number.isFinite(amount) || amount < 0) return null;
        try {
          const digits = new Intl.NumberFormat(this.config.locale || 'en', { style: 'currency', currency }).resolvedOptions().maximumFractionDigits;
          const unit = digits === 0 ? 100 : 1;
          const converted = Math.round(amount * rate / unit) * unit;
          return Number.isSafeInteger(converted) ? converted : null;
        } catch (_) { return null; }
      }

      quoteFor(variant) {
        if (this.bundleMode) {
          const number = Number(variant.title.match(/^Buy ([1-3])$/)?.[1]);
          const saving = this.convertUSD(this.bundleDiscounts[number - 1]);
          if (saving === null || saving > variant.price) return null;
          const price = variant.price - saving;
          return { price: this.formatMoney(price, this.config.presentmentCurrency), compare: this.formatMoney(variant.price, this.config.presentmentCurrency), saving: this.formatMoney(saving, this.config.presentmentCurrency, true), priceAmount: price, compareAmount: variant.price, savingAmount: saving };
        }
        const serverPrice = this.config.prices[String(variant.id)];
        const currency = this.config.presentmentCurrency;
        if (!currency || currency === 'USD' || this.config.shopCurrency !== 'USD') return serverPrice;
        const convertedDiscount = this.convertUSD(Number(this.config.displayDiscountCents));
        if (convertedDiscount === null) return serverPrice;
        const saving = Math.min(variant.price, convertedDiscount);
        const price = variant.price - saving;
        return {
          price: this.formatMoney(price, currency), compare: this.formatMoney(variant.price, currency),
          saving: this.formatMoney(saving, currency, true),
          priceAmount: price, compareAmount: variant.price, savingAmount: saving
        };
      }

      renderGifts() {
        if (!this.offer) return;
        const quantity = this.selectedTier;
        const currency = this.config.presentmentCurrency;
        const values = [['[data-wtmo-box-value]', 1000 * quantity], ['[data-wtmo-card-value]', 500 * quantity], ['[data-wtmo-guide-value]', 1000], ['[data-shipping-price]', 500], ['[data-wtmo-gift-value]', 1500 * quantity + 1500]];
        values.forEach(([selector, base]) => {
          const node = this.find(selector); if (!node) return;
          const converted = this.convertUSD(base);
          const total = selector === '[data-wtmo-gift-value]';
          node.textContent = this.formatMoney(converted === null ? base : converted, converted === null ? 'USD' : currency, total) + (total ? ' VALUE' : '');
        });
        ['box', 'card'].forEach(kind => {
          const badge = this.find(`[data-wtmo-${kind}-quantity]`);
          badge.hidden = quantity === 1; badge.textContent = quantity === 1 ? '' : 'x' + quantity;
          this.find(`[data-wtmo-${kind}-noun]`).textContent = kind === 'box' ? (quantity === 1 ? 'Box' : 'Boxes') : (quantity === 1 ? 'Card' : 'Cards');
        });
        const date = this.find('[data-bsp-ship-date]');
        if (date) { const tomorrow = new Date(); tomorrow.setDate(tomorrow.getDate() + 1); date.textContent = new Intl.DateTimeFormat(this.config.locale || 'en', { month: 'short', day: 'numeric' }).format(tomorrow); }
      }

      formatMoney(amount, currency, compact = false) {
        const options = { style: 'currency', currency, currencyDisplay: 'narrowSymbol' };
        if (compact && amount % 100 === 0) options.minimumFractionDigits = 0;
        const value = new Intl.NumberFormat(this.config.locale || 'en', options).format(amount / 100);
        return (currency === 'USD' || currency === 'GBP') && currency === this.config.presentmentCurrency ? value : value + ' ' + currency;
      }

      renderPrice(variant) {
        if (!variant) return;
        this.currentVariant = variant;
        [1, 2, 3].forEach(tier => {
          const quote = this.tierQuote(tier);
          this.find(`[data-bsp-tier-price="${tier}"]`).textContent = quote ? this.plainMoney(quote.price) : '—';
          this.find(`[data-bsp-tier-compare="${tier}"]`).textContent = quote ? this.plainMoney(quote.compare) : '';
        });
        const price = this.tierQuote(this.selectedTier);
        this.lastQuote = price;
        this.renderGifts();
        if (!price) return;
        this.find('[data-bsp-price]').textContent = this.plainMoney(price.price);
        if (this.submitPrice) this.submitPrice.textContent = this.plainMoney(price.price);
        const compare = this.find('[data-bsp-compare]');
        compare.textContent = this.plainMoney(price.compare);
        compare.hidden = !(price.compareAmount > price.priceAmount);
        if (this.savingBadge) {
          this.savingBadge.hidden = !(price.savingAmount > 0);
          if (price.savingAmount > 0) this.savingBadge.querySelector('[data-bsp-saving-text]').textContent = this.selectedTier === 1
            ? `50% ${this.strings.offLabel}`
            : this.strings.amountOffTemplate.replace('[amount]', this.plainMoney(price.saving));
        }
      }
      plainMoney(value) {
        const template = document.createElement('template');
        template.innerHTML = String(value || '');
        return template.content.textContent;
      }
      renderGallery() {
        if (!this.previewActivated) return;
        const pairs = this.selections.slice(0, this.selectedTier);
        const signature = JSON.stringify(pairs);
        this.findAll('[data-bsp-native-preview]').forEach(host => {
          if (host.dataset.selection === signature) return;
          host.dataset.selection = signature;
          host.dataset.count = String(pairs.length);
          const fragment = document.createDocumentFragment();
          pairs.forEach((pair, index) => {
            const figure = document.createElement('figure');
            figure.className = 'bsn-preview-ring';
            const frame = document.createElement('div');
            frame.className = 'bsn-preview-image';
            const label = this.strings.previewRings[index];
            const sources = [this.config.baseImageUrl, ...['first', 'second'].map((side, i) => this.overlayUrls[side][this.monthFor(pair[i])])];
            sources.forEach((source, i) => {
              if (!source) return;
              const img = document.createElement('img');
              img.src = source; img.width = 1024; img.height = 1024;
              img.alt = i === 0 ? `${label}: ${pair.filter(Boolean).join(' / ')}` : '';
              if (i) img.dataset.bspRaster = i === 1 ? 'first' : 'second';
              frame.appendChild(img);
            });
            figure.appendChild(frame);
            if (pairs.length > 1) {
              const caption = document.createElement('figcaption');
              caption.textContent = label;
              figure.appendChild(caption);
            }
            fragment.appendChild(figure);
          });
          host.replaceChildren(fragment);
        });
      }

      closePickers(returnFocus = false) {
        this.querySelectorAll('[data-bsp-open][aria-expanded="true"]').forEach(button => {
          button.setAttribute('aria-expanded', 'false');
          document.getElementById(button.getAttribute('aria-controls')).hidden = true;
          if (returnFocus) button.focus();
        });
      }

      syncPickers() {
        this.querySelectorAll('[data-bsp-card]').forEach(card => {
          const tier = Number(card.dataset.bspCard);
          card.classList.toggle('is-active', tier === this.selectedTier);
          card.querySelector('[data-bsp-tier]').setAttribute('aria-expanded', String(this.offerOpened && tier === this.selectedTier));
          card.querySelector('[data-bsp-panel]').hidden = !this.offerOpened || tier !== this.selectedTier;
        });
        this.querySelectorAll('[data-bsp-open]').forEach(button => {
          const value = this.selections[Number(button.dataset.ring) - 1][button.dataset.side === 'first' ? 0 : 1];
          button.querySelector('[data-bsp-value]').textContent = value ? value.split(' (')[0] : this.strings.choose;
          const gem = button.querySelector('.concept-gem');
          gem.hidden = false;
          if (value) gem.style.setProperty('--stone', this.colorFor(value));
          else gem.style.removeProperty('--stone');
        });
        this.choices.forEach(choice => {
          const value = this.selections[Number(choice.dataset.ring) - 1][choice.dataset.bspOption === 'first' ? 0 : 1];
          choice.setAttribute('aria-selected', String(value === choice.value));
        });
      }

      tierQuote(number) {
        if (this.bundleMode) return this.quoteFor(this.catalog.get(number));
        if (number === 1) return this.quoteFor(this.selectedVariants?.[0] || this.config.variants[0]);
        const price = this.convertUSD(this.bundleTotals[number - 1]);
        const compare = Array.from({ length: number }, (_, i) => this.selectedVariants?.[i] || this.config.variants[0]).reduce((sum, variant) => sum + variant.price, 0);
        if (price === null || compare === null) return null;
        return { price: this.formatMoney(price, this.config.presentmentCurrency), compare: this.formatMoney(compare, this.config.presentmentCurrency), saving: this.formatMoney(compare - price, this.config.presentmentCurrency, true), priceAmount: price, compareAmount: compare, savingAmount: compare - price };
      }

    });
  }


